import type { ActionType, Role } from "./types";

export type VoiceLineGroup = "actions" | "blocks" | "responses";

export type VoiceLineId =
  | `action:${ActionType}`
  | "block:patronage:Regent"
  | "block:seize:Corsair"
  | "block:seize:Envoy"
  | "block:silence:Sentinel"
  | "response:challenge";

export interface VoiceLinePrompt {
  id: VoiceLineId;
  group: VoiceLineGroup;
  title: string;
  trigger: string;
  example: string;
}

export interface StoredVoiceLine {
  id: VoiceLineId;
  blob: Blob;
  cleanupVersion: 1;
  cleanupMode?: "trimmed" | "raw-fallback";
  cleanupDiagnostic?: string;
  storageMode?: "persistent" | "session";
  storageDiagnostic?: string;
  durationMs: number;
  sourceDurationMs?: number;
  leadingTrimMs?: number;
  trailingTrimMs?: number;
  sourceMimeType?: string;
  sourceBytes?: number;
  sourceChunkCount?: number;
  recordedAt: number;
}

export const VOICE_LINE_PROMPTS: VoiceLinePrompt[] = [
  {
    id: "action:allowance",
    group: "actions",
    title: "Income",
    trigger: "Take 1 Coin",
    example: "Taking income."
  },
  {
    id: "action:patronage",
    group: "actions",
    title: "Foreign Aid",
    trigger: "Take 2 Coins",
    example: "Taking Foreign Aid."
  },
  {
    id: "action:banish",
    group: "actions",
    title: "Coup",
    trigger: "Pay 7 Coins",
    example: "Coup."
  },
  {
    id: "action:levy",
    group: "actions",
    title: "Tax as Duke",
    trigger: "Take 3 Coins",
    example: "Three as Duke."
  },
  {
    id: "action:silence",
    group: "actions",
    title: "Assassinate",
    trigger: "Pay 3 Coins",
    example: "Assassinating."
  },
  {
    id: "action:seize",
    group: "actions",
    title: "Steal as Captain",
    trigger: "Take up to 2",
    example: "Taking two as Captain."
  },
  {
    id: "action:exchange",
    group: "actions",
    title: "Exchange as Ambassador",
    trigger: "Draw 2, return 2",
    example: "Pulling from the deck as Ambassador."
  },
  {
    id: "block:patronage:Regent",
    group: "blocks",
    title: "Duke blocks Foreign Aid",
    trigger: "Block Foreign Aid",
    example: "Blocking Foreign Aid as Duke."
  },
  {
    id: "block:seize:Corsair",
    group: "blocks",
    title: "Captain blocks Steal",
    trigger: "Block Steal",
    example: "Blocking as Captain."
  },
  {
    id: "block:seize:Envoy",
    group: "blocks",
    title: "Ambassador blocks Steal",
    trigger: "Block Steal",
    example: "Blocking as Ambassador."
  },
  {
    id: "block:silence:Sentinel",
    group: "blocks",
    title: "Contessa blocks Assassination",
    trigger: "Block Assassinate",
    example: "Blocking as Contessa."
  },
  {
    id: "response:challenge",
    group: "responses",
    title: "Challenge",
    trigger: "Call a bluff",
    example: "I’m calling you out."
  }
];

const DATABASE_NAME = "veil-local-voice-lines";
const DATABASE_VERSION = 2;
const STORE_NAME = "clips";
const HEALTH_STORE_NAME = "health";
const playbackUrls = new Map<VoiceLineId, string>();
const sessionVoiceLines = new Map<VoiceLineId, StoredVoiceLine>();

export interface VoiceStorageStatus {
  mode: "persistent" | "session";
  diagnostic?: string;
}

export interface VoiceLineLoadResult {
  records: StoredVoiceLine[];
  storage: VoiceStorageStatus;
}

export interface VoiceLineSaveResult {
  record: StoredVoiceLine;
  storage: VoiceStorageStatus;
}

export type PersistedVoiceLineRecord = Omit<
  StoredVoiceLine,
  "blob" | "storageMode" | "storageDiagnostic"
> & {
  blob?: Blob;
  blobData?: ArrayBuffer;
  blobType?: string;
};

let storageProbePromise: Promise<VoiceStorageStatus> | null = null;

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new DOMException("IndexedDB is unavailable.", "NotSupportedError"));
      return;
    }

    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    let settled = false;

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains(HEALTH_STORE_NAME)) {
        database.createObjectStore(HEALTH_STORE_NAME, { keyPath: "id" });
      }
    };
    request.onsuccess = () => {
      if (settled) {
        request.result.close();
        return;
      }
      settled = true;
      resolve(request.result);
    };
    request.onerror = () => {
      if (settled) return;
      settled = true;
      reject(request.error ?? new Error("Voice storage could not be opened."));
    };
    request.onblocked = () => {
      if (settled) return;
      settled = true;
      reject(new DOMException("Voice storage upgrade was blocked.", "BlockedError"));
    };
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Voice storage request failed."));
  });
}

function waitForTransaction(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error("Voice storage transaction was cancelled."));
    transaction.onerror = () => reject(transaction.error ?? new Error("Voice storage transaction failed."));
  });
}

function storageDiagnostic(reason: unknown): string {
  if (reason instanceof DOMException) return reason.name || "DOMException";
  if (reason instanceof Error) return reason.name || "Error";
  if (
    reason &&
    typeof reason === "object" &&
    "name" in reason &&
    typeof reason.name === "string"
  ) {
    return reason.name || "StorageError";
  }
  return "UnknownStorageError";
}

function setPlaybackUrl(id: VoiceLineId, blob: Blob) {
  const previous = playbackUrls.get(id);
  if (previous) URL.revokeObjectURL(previous);
  playbackUrls.set(id, URL.createObjectURL(blob));
}

export async function serializeVoiceLineForStorage(
  record: StoredVoiceLine
): Promise<PersistedVoiceLineRecord> {
  const {
    blob,
    storageMode: _storageMode,
    storageDiagnostic: _storageDiagnostic,
    ...metadata
  } = record;
  return {
    ...metadata,
    blobData: await blob.arrayBuffer(),
    blobType: blob.type
  };
}

export function deserializeVoiceLineFromStorage(value: unknown): StoredVoiceLine | null {
  if (!value || typeof value !== "object") return null;
  const persisted = value as PersistedVoiceLineRecord;
  if (
    persisted.cleanupVersion !== 1 ||
    !VOICE_LINE_PROMPTS.some((prompt) => prompt.id === persisted.id)
  ) {
    return null;
  }

  const blob =
    persisted.blob instanceof Blob
      ? persisted.blob
      : persisted.blobData instanceof ArrayBuffer
        ? new Blob([persisted.blobData], { type: persisted.blobType || "audio/mp4" })
        : null;
  if (!blob || blob.size === 0) return null;

  const {
    blob: _legacyBlob,
    blobData: _blobData,
    blobType: _blobType,
    ...metadata
  } = persisted;
  return {
    ...metadata,
    blob,
    storageMode: "persistent"
  };
}

async function persistentRecords(database: IDBDatabase): Promise<StoredVoiceLine[]> {
  const transaction = database.transaction(STORE_NAME, "readonly");
  const completion = waitForTransaction(transaction);
  const [values] = await Promise.all([
    requestResult(transaction.objectStore(STORE_NAME).getAll()),
    completion
  ]);
  return (values as unknown[])
    .map(deserializeVoiceLineFromStorage)
    .filter((record): record is StoredVoiceLine => Boolean(record));
}

async function persistAndVerify(database: IDBDatabase, record: StoredVoiceLine): Promise<void> {
  const serialized = await serializeVoiceLineForStorage(record);
  const writeTransaction = database.transaction(STORE_NAME, "readwrite");
  const writeCompletion = waitForTransaction(writeTransaction);
  const writeRequest = writeTransaction.objectStore(STORE_NAME).put(serialized);
  await Promise.all([requestResult(writeRequest), writeCompletion]);

  const readTransaction = database.transaction(STORE_NAME, "readonly");
  const readCompletion = waitForTransaction(readTransaction);
  const [storedValue] = await Promise.all([
    requestResult(readTransaction.objectStore(STORE_NAME).get(record.id)),
    readCompletion
  ]);
  const restored = deserializeVoiceLineFromStorage(storedValue);
  if (
    !restored ||
    restored.blob.size !== record.blob.size ||
    restored.blob.type !== record.blob.type
  ) {
    throw new DOMException("Voice storage round trip did not match.", "DataError");
  }
}

async function runStorageProbe(): Promise<VoiceStorageStatus> {
  let database: IDBDatabase | null = null;
  try {
    database = await openDatabase();
    const probeBytes = new Uint8Array([0x56, 0x45, 0x49, 0x4c]).buffer;
    const writeTransaction = database.transaction(HEALTH_STORE_NAME, "readwrite");
    const writeCompletion = waitForTransaction(writeTransaction);
    const writeRequest = writeTransaction.objectStore(HEALTH_STORE_NAME).put({
      id: "array-buffer-roundtrip",
      data: probeBytes,
      checkedAt: Date.now()
    });
    await Promise.all([requestResult(writeRequest), writeCompletion]);

    const readTransaction = database.transaction(HEALTH_STORE_NAME, "readonly");
    const readCompletion = waitForTransaction(readTransaction);
    const [storedProbe] = await Promise.all([
      requestResult(
        readTransaction.objectStore(HEALTH_STORE_NAME).get("array-buffer-roundtrip")
      ),
      readCompletion
    ]);
    const result = storedProbe as { data?: ArrayBuffer } | undefined;
    if (
      !(result?.data instanceof ArrayBuffer) ||
      result.data.byteLength !== probeBytes.byteLength
    ) {
      throw new DOMException("Storage probe data did not round trip.", "DataError");
    }

    const deleteTransaction = database.transaction(HEALTH_STORE_NAME, "readwrite");
    const deleteCompletion = waitForTransaction(deleteTransaction);
    deleteTransaction.objectStore(HEALTH_STORE_NAME).delete("array-buffer-roundtrip");
    await deleteCompletion;
    return { mode: "persistent" };
  } catch (reason) {
    return { mode: "session", diagnostic: storageDiagnostic(reason) };
  } finally {
    database?.close();
  }
}

export function inspectVoiceLineStorage(): Promise<VoiceStorageStatus> {
  storageProbePromise ??= runStorageProbe();
  return storageProbePromise;
}

export async function loadVoiceLineState(): Promise<VoiceLineLoadResult> {
  let storage = await inspectVoiceLineStorage();
  const merged = new Map<VoiceLineId, StoredVoiceLine>();

  if (storage.mode === "persistent") {
    let database: IDBDatabase | null = null;
    try {
      database = await openDatabase();
      for (const record of await persistentRecords(database)) merged.set(record.id, record);
    } catch (reason) {
      storage = { mode: "session", diagnostic: storageDiagnostic(reason) };
    } finally {
      database?.close();
    }
  }

  for (const record of sessionVoiceLines.values()) merged.set(record.id, record);
  return { records: [...merged.values()], storage };
}

export async function listVoiceLines(): Promise<StoredVoiceLine[]> {
  return (await loadVoiceLineState()).records;
}

export async function preloadVoiceLines(): Promise<void> {
  const records = await listVoiceLines();
  records.forEach((record) => setPlaybackUrl(record.id, record.blob));
}

export async function saveVoiceLine(record: StoredVoiceLine): Promise<VoiceLineSaveResult> {
  let database: IDBDatabase | null = null;
  try {
    database = await openDatabase();
    const persistentRecord: StoredVoiceLine = {
      ...record,
      storageMode: "persistent",
      storageDiagnostic: undefined
    };
    await persistAndVerify(database, persistentRecord);
    sessionVoiceLines.delete(record.id);
    setPlaybackUrl(record.id, record.blob);
    return { record: persistentRecord, storage: { mode: "persistent" } };
  } catch (reason) {
    const diagnostic = storageDiagnostic(reason);
    const sessionRecord: StoredVoiceLine = {
      ...record,
      storageMode: "session",
      storageDiagnostic: diagnostic
    };
    sessionVoiceLines.set(record.id, sessionRecord);
    setPlaybackUrl(record.id, record.blob);
    console.info("[voice-lines] Using session-only storage after IndexedDB failure", {
      origin: typeof window === "undefined" ? "non-window-runtime" : window.location.origin,
      databaseVersion: DATABASE_VERSION,
      errorName: diagnostic,
      mimeType: record.blob.type,
      sizeBytes: record.blob.size
    });
    return {
      record: sessionRecord,
      storage: { mode: "session", diagnostic }
    };
  } finally {
    database?.close();
  }
}

export async function deleteVoiceLine(id: VoiceLineId): Promise<VoiceStorageStatus> {
  sessionVoiceLines.delete(id);
  const url = playbackUrls.get(id);
  if (url) URL.revokeObjectURL(url);
  playbackUrls.delete(id);

  let database: IDBDatabase | null = null;
  try {
    database = await openDatabase();
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const completion = waitForTransaction(transaction);
    transaction.objectStore(STORE_NAME).delete(id);
    await completion;
    return { mode: "persistent" };
  } catch (reason) {
    return { mode: "session", diagnostic: storageDiagnostic(reason) };
  } finally {
    database?.close();
  }
}

export function preferredRecordingMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  if (typeof MediaRecorder.isTypeSupported !== "function") return "audio/mp4";
  return [
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus"
  ].find((type) => MediaRecorder.isTypeSupported(type));
}

export function playVoiceLine(id: VoiceLineId): boolean {
  const url = playbackUrls.get(id);
  if (!url) return false;
  const audio = new Audio(url);
  audio.volume = 1;
  void audio.play().catch(() => {
    // Playback is deliberately best-effort; the game action must never fail
    // because a browser blocks or interrupts local audio.
  });
  return true;
}

export function voiceLineForAction(type: ActionType): VoiceLineId {
  return `action:${type}`;
}

export function voiceLineForBlock(type: ActionType | undefined, role: Role): VoiceLineId | null {
  if (type === "patronage" && role === "Regent") return "block:patronage:Regent";
  if (type === "seize" && role === "Corsair") return "block:seize:Corsair";
  if (type === "seize" && role === "Envoy") return "block:seize:Envoy";
  if (type === "silence" && role === "Sentinel") return "block:silence:Sentinel";
  return null;
}
